import {asDefined, isAbsent, isDefined, Optional, RuntimeNotifier, Terminable, UUID} from "@opendaw/lib-std"
import {Promises} from "@opendaw/lib-runtime"
import {DragAndDrop} from "@/ui/DragAndDrop"
import {AnyDragData} from "@/ui/AnyDragData"
import {
    AudioBusBoxAdapter,
    AudioUnitBoxAdapter,
    DeviceHost,
    Devices,
    InstrumentBox,
    InstrumentCompositeCellBoxAdapter,
    InstrumentFactories,
    InstrumentFactory,
    PresetDecoder,
    PresetHeader
} from "@opendaw/studio-adapters"
import {InsertMarker} from "@/ui/components/InsertMarker"
import {EffectBox, EffectFactories, PresetSource, Project} from "@opendaw/studio-core"
import {PresetApplication} from "@/ui/browse/PresetApplication"
import {IndexedBox} from "@opendaw/lib-box"
import {AudioUnitBox} from "@opendaw/studio-boxes"

export namespace DevicePanelDragAndDrop {
    export const install = (project: Project,
                            editors: HTMLElement,
                            midiEffectsContainer: HTMLElement,
                            instrumentContainer: HTMLElement,
                            audioEffectsContainer: HTMLElement): Terminable => {
        const insertMarker: HTMLElement = InsertMarker()
        const {editing, boxAdapters, userEditingManager} = project
        return DragAndDrop.installTarget(editors, {
            drag: (event: DragEvent, dragData: AnyDragData): boolean => {
                instrumentContainer.style.opacity = "1.0"
                // A drop over a composite's branch list goes INTO a branch (or makes a new one), not into this
                // parent chain. Its own target handles it, so suppress this chain's insert marker while there.
                if (event.target instanceof Element && isDefined(event.target.closest("[data-composite-drop]"))) {
                    if (insertMarker.isConnected) {insertMarker.remove()}
                    return false
                }
                const editingDeviceChain = userEditingManager.audioUnit.get()
                if (editingDeviceChain.isEmpty()) {return false}
                const deviceHost = boxAdapters.adapterFor(editingDeviceChain.unwrap().box, Devices.isHost)
                const {type} = dragData
                if (type === "preset") {
                    if (dragData.category === "audio-unit" && deviceHost.isAudioUnit) {
                        instrumentContainer.style.opacity = "0.5"
                        return true
                    }
                    if (dragData.category === "instrument" && deviceHost.isAudioUnit
                        && !deviceHost.inputAdapter.mapOr(input => input instanceof AudioBusBoxAdapter, false)) {
                        instrumentContainer.style.opacity = "0.5"
                        return true
                    }
                    if (dragData.category === "audio-effect" || dragData.category === "audio-effect-chain") {
                        if (!DeviceHost.takesEffect(deviceHost, "audio")) {return false}
                        const [_index, successor] = DragAndDrop.findInsertLocation(event, audioEffectsContainer)
                        audioEffectsContainer.insertBefore(insertMarker, successor)
                        return true
                    }
                    if (dragData.category === "midi-effect" || dragData.category === "midi-effect-chain") {
                        if (!DeviceHost.takesEffect(deviceHost, "midi")) {return false}
                        const [_index, successor] = DragAndDrop.findInsertLocation(event, midiEffectsContainer)
                        midiEffectsContainer.insertBefore(insertMarker, successor)
                        return true
                    }
                    return false
                }
                let container: HTMLElement
                if (type === "audio-effect") {
                    if (!DeviceHost.takesEffect(deviceHost, "audio")) {return false}
                    container = audioEffectsContainer
                } else if (type === "midi-effect") {
                    if (!DeviceHost.takesEffect(deviceHost, "midi")) {return false}
                    container = midiEffectsContainer
                } else if (type === "instrument" && deviceHost instanceof InstrumentCompositeCellBoxAdapter) {
                    if (!isDefined(dragData.device)) {return false}
                    const factory: Optional<InstrumentFactory> = InstrumentFactories.Named[dragData.device]
                    if (!isDefined(factory) || !InstrumentFactories.isLayerInstrument(factory)) {return false}
                    instrumentContainer.style.opacity = "0.5"
                    return true
                } else if (type === "instrument" && deviceHost.isAudioUnit) {
                    if (dragData.device === null) {return false}
                    if (deviceHost.inputAdapter.mapOr(input => input instanceof AudioBusBoxAdapter, false)) {
                        return false
                    }
                    instrumentContainer.style.opacity = "0.5"
                    return true
                } else {
                    return false
                }
                const [_index, successor] = DragAndDrop.findInsertLocation(event, container)
                container.insertBefore(insertMarker, successor)
                return true
            },
            drop: (event: DragEvent, dragData: AnyDragData): void => {
                instrumentContainer.style.opacity = "1.0"
                if (insertMarker.isConnected) {insertMarker.remove()}
                const {type} = dragData
                if (type === "preset") {
                    const dropIndex = dragData.category === "audio-effect" || dragData.category === "audio-effect-chain"
                        ? DragAndDrop.findInsertLocation(event, audioEffectsContainer)[0]
                        : dragData.category === "midi-effect" || dragData.category === "midi-effect-chain"
                            ? DragAndDrop.findInsertLocation(event, midiEffectsContainer)[0]
                            : 0
                    handlePresetDrop(project, dragData, dropIndex).catch(console.warn)
                    return
                }
                if (type !== "midi-effect" && type !== "audio-effect" && type !== "instrument") {return}
                const editingDeviceChain = userEditingManager.audioUnit.get()
                if (editingDeviceChain.isEmpty()) {return}
                const deviceHost = boxAdapters.adapterFor(editingDeviceChain.unwrap("editingDeviceChain isEmpty").box, Devices.isHost)
                if (type === "instrument" && deviceHost instanceof AudioUnitBoxAdapter) {
                    if (dragData.device === null) {return}
                    const inputBox = deviceHost.inputField.pointerHub.incoming().at(0)?.box
                    if (isAbsent(inputBox)) {
                        console.warn("No instrument to replace")
                        return
                    }
                    const namedElement = InstrumentFactories.Named[dragData.device]
                    const factory = asDefined(namedElement, `Unknown: '${dragData.device}'`) as InstrumentFactory
                    editing.modify(() => {
                        // a new Composite wraps the chain it lands on instead of replacing its instrument
                        const attempt = factory === InstrumentFactories.InstrumentComposite
                            ? project.api.wrapInstrumentIntoComposite(inputBox as InstrumentBox)
                            : project.api.replaceMIDIInstrument(inputBox as InstrumentBox, factory)
                        if (attempt.isFailure()) {console.debug(attempt.failureReason())}
                    })
                    return
                }
                if (type === "instrument" && deviceHost instanceof InstrumentCompositeCellBoxAdapter) {
                    if (!isDefined(dragData.device)) {return}
                    const factory = asDefined(InstrumentFactories.Named[dragData.device], `Unknown: '${dragData.device}'`) as InstrumentFactory
                    editing.modify(() => {
                        const attempt = project.api.setLayerInstrument(deviceHost.box, factory)
                        if (attempt.isFailure()) {console.debug(attempt.failureReason())}
                    })
                    return
                }
                if (type === "instrument") {return} // an instrument drop onto any other host: nothing to do
                const accepts = type === "audio-effect" ? "audio" : "midi"
                // The `drag` gate already refused a host that takes no chain of this kind; re-checked here
                // because `drop` is reachable on its own.
                const optField = DeviceHost.chainFieldOf(deviceHost, accepts)
                if (optField.isEmpty()) {return}
                const field = optField.unwrap()
                const container = accepts === "audio" ? audioEffectsContainer : midiEffectsContainer
                const [index] = DragAndDrop.findInsertLocation(event, container)
                if (dragData.uuids === null) {
                    editing.modify(() => {
                        const factory = EffectFactories.MergedNamed[dragData.device]
                        project.api.insertEffect(field, factory, index)
                    })
                } else {
                    const uuids = dragData.uuids
                    if (uuids.length === 0) {return}
                    const deviceType = accepts === "audio" ? "audio-effect" : "midi-effect"
                    const boxes = uuids
                        .map(uuidStr => project.boxGraph.findBox(UUID.parse(uuidStr)).unwrapOrNull())
                        .filter(isDefined)
                        .filter((box): box is EffectBox => box.tags.deviceType === deviceType)
                    if (boxes.length === 0) {return}
                    if (DragAndDrop.isCopy(event, dragData)) {
                        editing.modify(() => project.api.copyEffects(field, boxes, index))
                        return
                    }
                    const sameChain = boxes.every(box => box.host.targetVertex.mapOr(vertex => vertex === field, false))
                    if (sameChain) {
                        // A plain reorder WITHIN this chain: keep the slot-shuffling semantics.
                        const startIndices = boxes.map(box => box.index.getValue()).toSorted((a, b) => a - b)
                        editing.modify(() => IndexedBox.moveIndices(field, startIndices, index))
                    } else {
                        // A cross-chain MOVE (e.g. an effect dragged out of a composite branch): re-home it here.
                        editing.modify(() => project.api.moveEffects(field, boxes, index))
                    }
                }
            },
            enter: () => {},
            leave: () => {
                instrumentContainer.style.opacity = "1.0"
                if (insertMarker.isConnected) {insertMarker.remove()}
            }
        })
    }

    const resolveKeepTimeline = async (bytes: ArrayBuffer, targetAudioUnit: AudioUnitBox): Promise<boolean | "abort"> => {
        const sourceHasTimeline = PresetDecoder.peekHasTimeline(bytes)
        const targetHasTimeline = targetAudioUnit.tracks.pointerHub.incoming().length > 0
        if (!sourceHasTimeline) {return true}
        if (!targetHasTimeline) {return false}
        const replace = await Promises.tryCatch(RuntimeNotifier.approve({
            headline: "Replace Timeline?",
            message: "This preset includes timeline content (clips, regions, automation). "
                + "Replace the existing timeline on this audio unit, or keep your current one?",
            approveText: "Replace",
            cancelText: "Keep"
        }))
        if (replace.status === "rejected") {return "abort"}
        return !replace.value
    }

    const handlePresetDrop = async (project: Project,
                                    dragData: { category: string, source: PresetSource, uuid: UUID.String },
                                    dropIndex: number): Promise<void> => {
        const editing = project.userEditingManager.audioUnit.get()
        if (editing.isEmpty()) {return}
        const host = project.boxAdapters.adapterFor(editing.unwrap().box, Devices.isHost)
        const targetAudioUnit = host.audioUnitBoxAdapter().box
        const load = await Promises.tryCatch(PresetApplication.loadBytes(dragData.uuid, dragData.source))
        if (load.status === "rejected") {
            console.warn(load.error)
            RuntimeNotifier.notify({message: "Cannot load preset.", icon: "Warning"})
            return
        }
        if (host instanceof InstrumentCompositeCellBoxAdapter
            && (dragData.category === "audio-unit" || dragData.category === "instrument")) {
            if (dragData.category === "audio-unit") {
                RuntimeNotifier.notify({message: "A rack preset cannot be loaded into a layer.", icon: "Warning"})
                return
            }
            project.editing.modify(() => {
                const attempt = PresetDecoder.replaceLayerInstrument(load.value, host.box)
                if (attempt.isFailure()) {
                    RuntimeNotifier.notify({message: "Cannot apply preset.", icon: "Warning"})
                }
            })
            project.loadScriptDevices()
            return
        }
        if (dragData.category === "audio-unit") {
            const keepTimeline = await resolveKeepTimeline(load.value, targetAudioUnit)
            if (keepTimeline === "abort") {return}
            project.editing.modify(() => {
                const attempt = PresetDecoder.replaceAudioUnit(load.value, targetAudioUnit, {keepTimeline})
                if (attempt.isFailure()) {
                    RuntimeNotifier.notify({message: "Cannot apply preset.", icon: "Warning"})
                }
            })
            project.loadScriptDevices()
            return
        }
        if (dragData.category === "instrument") {
            const keepTimeline = await resolveKeepTimeline(load.value, targetAudioUnit)
            if (keepTimeline === "abort") {return}
            project.editing.modify(() => {
                const attempt = PresetDecoder.replaceAudioUnit(load.value, targetAudioUnit,
                    {keepMIDIEffects: true, keepAudioEffects: true, keepTimeline})
                if (attempt.isFailure()) {
                    RuntimeNotifier.notify({message: "Cannot apply preset.", icon: "Warning"})
                }
            })
            project.loadScriptDevices()
            return
        }
        if (dragData.category === "audio-effect" || dragData.category === "midi-effect"
            || dragData.category === "audio-effect-chain" || dragData.category === "midi-effect-chain") {
            const isMidi = dragData.category === "midi-effect" || dragData.category === "midi-effect-chain"
            const chainKind = isMidi ? PresetHeader.ChainKind.Midi : PresetHeader.ChainKind.Audio
            // The chain being EDITED (a composite branch cell when inside one), not the audio unit, so a preset
            // dropped while inside a branch lands in the branch.
            const field = DeviceHost.chainFieldOf(host, isMidi ? "midi" : "audio")
            if (field.isEmpty()) {
                RuntimeNotifier.notify({message: "Cannot apply preset.", icon: "Warning"})
                return
            }
            project.editing.modify(() => {
                const attempt = PresetDecoder.insertEffectChain(load.value, field.unwrap("effect chain"), dropIndex, chainKind)
                if (attempt.isFailure()) {
                    RuntimeNotifier.notify({message: "Cannot apply preset.", icon: "Warning"})
                }
            })
            project.loadScriptDevices()
            return
        }
        console.debug(`Preset drop for category '${dragData.category}' not yet implemented`)
    }
}